import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { api, setUnauthenticatedHandler, unwrap, type MeResponse, type UserInfo } from "../api/client";

/** Permission codes of the server (web spec §4.2). */
export type Permission =
  | "event.read" | "event.write" | "event.publish" | "restriction.write" | "poi.write" | "import.run"
  | "fieldReport.resolve" | "alert.acknowledge" | "route.test" | "history.read" | "heightCorrection.write"
  | "data.delete" | "user.manage" | "settings.manage";

interface Session {
  /** undefined while loading, null when not connected */
  me: MeResponse | null | undefined;
  user: UserInfo | null;
  can(p: Permission): boolean;
  isAdmin: boolean;
  setMe(me: MeResponse | null): void;
  refresh(): Promise<void>;
  logout(): Promise<void>;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null | undefined>(undefined);
  const refresh = useCallback(async () => {
    try {
      const r = await api.GET("/me");
      setMe(r.response.ok ? unwrap(r) : null);
    } catch {
      setMe(null);
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    setUnauthenticatedHandler(() => setMe(null));
    return () => setUnauthenticatedHandler(null);
  }, []);
  const value = useMemo<Session>(() => {
    const perms = new Set(me?.permissions ?? []);
    return {
      me,
      user: me?.user ?? null,
      can: (p) => perms.has(p),
      isAdmin: me?.user.role === "ADMIN",
      setMe,
      refresh,
      logout: async () => {
        try { await api.POST("/auth/logout"); } catch { /* the cookie is dropped anyway */ }
        setMe(null);
      },
    };
  }, [me, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("SessionProvider missing");
  return s;
}

/** Pages of the console: a session is required, the login page keeps the target (SW-AU-05). */
export function RequireAuth({ children, permission }: { children: ReactNode; permission?: Permission }) {
  const s = useSession();
  const loc = useLocation();
  if (s.me === undefined) return <div className="page muted">Chargement…</div>;
  if (s.me === null) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (permission && !s.can(permission)) return <div className="page"><div className="error">Accès réservé : droit {permission} requis.</div></div>;
  return <>{children}</>;
}
